import { useEffect, useMemo, useState } from "react";
import { BattleArena } from "../BattleArena";
import { useBattleReplay } from "../../hooks/useBattleReplay";
import { useGameStore } from "../../store/useGameStore";
import type { BattleLogEvent } from "../../types";
import type { BattleBracketView, GuildBattleCurrentMatch, GuildBattleLiveView } from "../../api/guild";
import {
  guildBattleInfoMessage,
  isPalaceLineWarMatch,
  resolveAllyTeam,
  resolveGuildBattleOutcome,
  resolveViewerFighterUid,
} from "../../utils/guildBattleResult";
import { GuildBattleResultOverlay } from "./GuildBattleResultOverlay";
import { GuildBattleArenaWithBench, resolveLineWarBenches } from "./GuildBattleReserveBench";
import { GuildTournamentBracket } from "./GuildTournamentBracket";

function guildMatchContextLabel(match: GuildBattleCurrentMatch): string | undefined {
  if (match.palace) {
    const seat = match.seat_index != null ? ` · Seat ${match.seat_index + 1}` : "";
    return `${match.palace}${seat}`;
  }
  if (match.guild_a_name && match.guild_b_name) {
    return `${match.guild_a_name} vs ${match.guild_b_name}`;
  }
  if (match.guild_a_name) return match.guild_a_name;
  return "Guild Battle";
}

interface GuildBattleReplayOverlayProps {
  match: GuildBattleCurrentMatch;
  matchQueue?: GuildBattleCurrentMatch[];
  /** Palace or platinum shared watch queue — play all matches, then onWatchComplete. */
  queueWatch?: boolean;
  /** @deprecated use queueWatch */
  palaceWatch?: boolean;
  heroClass?: string;
  heroName?: string;
  fallbackMaxHp: number;
  userId?: number;
  myGuildId?: number;
  battleBracket?: BattleBracketView | null;
  tournamentTree?: GuildBattleLiveView["tournament_tree"];
  onAck: (matchId: string, opts?: { warFinal?: boolean; showBracket?: boolean }) => void;
  onWatchComplete?: () => void;
  ackRequired?: boolean;
  onDismiss?: () => void;
}

interface MatchReplayBodyProps {
  match: GuildBattleCurrentMatch;
  heroClass?: string;
  heroName?: string;
  fallbackMaxHp: number;
  userId?: number;
  myGuildId?: number;
  battleBracket?: BattleBracketView | null;
  onDoneChange: (done: boolean) => void;
}

/** Fresh mount per match so useBattleReplay does not keep a stale log. */
function MatchReplayBody({
  match,
  heroClass,
  heroName,
  fallbackMaxHp,
  userId,
  myGuildId,
  battleBracket,
  onDoneChange,
}: MatchReplayBodyProps) {
  const log = (match.log ?? []) as BattleLogEvent[];
  const { visibleLog, done } = useBattleReplay(log);
  const allyTeam = resolveAllyTeam(match, myGuildId, userId);
  const viewerFighterUid = resolveViewerFighterUid(match, userId, myGuildId);
  const lineWar = isPalaceLineWarMatch(match);
  const lineWarBench = lineWar
    ? resolveLineWarBenches(battleBracket, match, allyTeam, visibleLog)
    : null;

  useEffect(() => {
    onDoneChange(done);
  }, [done, onDoneChange]);

  const arena = (
    <BattleArena
      visibleLog={visibleLog}
      heroClass={lineWar ? undefined : (heroClass as import("../../types").HeroClass | undefined)}
      heroName={lineWar ? undefined : heroName}
      fallbackMaxHp={fallbackMaxHp}
      battleType="MP_DUNGEON"
      battleContextLabel={guildMatchContextLabel(match)}
      forcePartyMode={lineWar}
      viewerPlayerUid={viewerFighterUid}
      allyTeam={allyTeam}
    />
  );

  if (lineWarBench) {
    return (
      <GuildBattleArenaWithBench bench={lineWarBench}>
        <div className="relative h-full min-h-0">{arena}</div>
      </GuildBattleArenaWithBench>
    );
  }
  return <div className="flex-1 relative min-h-0">{arena}</div>;
}

export function GuildBattleReplayOverlay({
  match: initialMatch,
  matchQueue,
  queueWatch: queueWatchProp,
  palaceWatch,
  heroClass,
  heroName,
  fallbackMaxHp,
  userId,
  myGuildId,
  battleBracket,
  tournamentTree,
  onAck,
  onWatchComplete,
  ackRequired = true,
  onDismiss,
}: GuildBattleReplayOverlayProps) {
  const queueWatch = Boolean(queueWatchProp ?? palaceWatch);
  const pushInfoMessages = useGameStore((s) => s.pushInfoMessages);
  const queue = useMemo(() => {
    if (queueWatch && matchQueue && matchQueue.length > 0) return matchQueue;
    return [initialMatch];
  }, [queueWatch, matchQueue, initialMatch]);

  const [queueIndex, setQueueIndex] = useState(0);
  const match = queue[Math.min(queueIndex, queue.length - 1)] ?? initialMatch;
  const [done, setDone] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const matchKey = `${match.id}-${match.palace ?? ""}-${match.seat_index ?? ""}-${queueIndex}`;

  useEffect(() => {
    setQueueIndex(0);
  }, [queue.map((m) => m.id).join("|")]);

  useEffect(() => {
    setDone(false);
    setShowResult(false);
    setConfirmed(false);
  }, [matchKey]);

  useEffect(() => {
    if (!done || showResult || confirmed) return;
    // Shared watch queue or non-blocking watch: auto-advance.
    if (queueWatch || !ackRequired) {
      const t = setTimeout(() => {
        const outcome = resolveGuildBattleOutcome(match, userId, myGuildId);
        if (isPalaceLineWarMatch(match) || match.type?.startsWith("platinum")) {
          pushInfoMessages([guildBattleInfoMessage(match, outcome)]);
        }
        if (queueWatch && queueIndex + 1 < queue.length) {
          setQueueIndex((i) => i + 1);
          return;
        }
        setConfirmed(true);
        if (queueWatch) {
          onWatchComplete?.();
        } else {
          onAck(match.id, { warFinal: true, showBracket: false });
        }
      }, 1800);
      return () => clearTimeout(t);
    }
    setShowResult(true);
  }, [
    done,
    showResult,
    confirmed,
    queueWatch,
    ackRequired,
    queueIndex,
    queue.length,
    match,
    userId,
    myGuildId,
    pushInfoMessages,
    onWatchComplete,
    onAck,
  ]);

  const label =
    match.type === "platinum_final" || match.type === "platinum_contender"
      ? `Platinum seat #${match.seat_index ?? "?"}`
      : `${match.guild_a_name ?? "Attackers"} vs ${match.guild_b_name ?? "Defenders"}`;

  const palaceTag = match.palace
    ? `${match.palace.charAt(0).toUpperCase()}${match.palace.slice(1)} Palace`
    : null;

  const handleConfirm = () => {
    if (confirmed) return;
    setConfirmed(true);
    const outcome = resolveGuildBattleOutcome(match, userId, myGuildId);
    pushInfoMessages([guildBattleInfoMessage(match, outcome)]);
    if (ackRequired) {
      onAck(match.id, { warFinal: true, showBracket: isPalaceLineWarMatch(match) });
    } else {
      onDismiss?.();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="px-4 py-2 bg-kingdom-purple border-b border-kingdom-mid text-center space-y-1">
        <p className="text-sm font-bold text-amber-200">
          {queueWatch || !ackRequired ? "⚔️ Guild Battle — live" : "⚔️ Guild Battle"}
        </p>
        {palaceTag && <p className="text-[10px] text-violet-300 uppercase tracking-wide">{palaceTag}</p>}
        <p className="text-xs text-gray-400">{label}</p>
        {queueWatch && queue.length > 1 && (
          <p className="text-[10px] text-gray-500">
            Battle {queueIndex + 1} / {queue.length}
          </p>
        )}
        {(battleBracket || tournamentTree) && !queueWatch && (
          <div className="max-h-28 overflow-y-auto text-left">
            <GuildTournamentBracket
              tree={tournamentTree ?? null}
              bracket={battleBracket ?? null}
              currentMatch={match}
              compact
            />
          </div>
        )}
      </div>
      <MatchReplayBody
        key={matchKey}
        match={match}
        heroClass={heroClass}
        heroName={heroName}
        fallbackMaxHp={fallbackMaxHp}
        userId={userId}
        myGuildId={myGuildId}
        battleBracket={battleBracket}
        onDoneChange={setDone}
      />
      {showResult && !queueWatch && (
        <GuildBattleResultOverlay
          outcome={resolveGuildBattleOutcome(match, userId, myGuildId)}
          subtitle={label}
          confirmLabel="OK"
          onConfirm={handleConfirm}
        />
      )}
      {done && queueWatch && queueIndex + 1 >= queue.length && confirmed && (
        <div className="px-4 py-3 bg-kingdom-purple border-t border-kingdom-mid text-center text-xs text-emerald-300">
          {match.type?.startsWith("platinum")
            ? "Platinum seats settled…"
            : "Palace wars complete — Platinum opening…"}
        </div>
      )}
      {done && !showResult && !queueWatch && ackRequired && (
        <div className="px-4 py-3 bg-kingdom-purple border-t border-kingdom-mid text-center text-xs text-emerald-300">
          Preparing results…
        </div>
      )}
      {done && !ackRequired && !queueWatch && !showResult && onDismiss && (
        <div className="px-4 py-3 bg-kingdom-purple border-t border-kingdom-mid text-center">
          <button
            type="button"
            onClick={onDismiss}
            className="px-6 py-2 rounded-xl bg-kingdom-mid text-white text-sm font-bold"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
